import { useFetch } from '@/composables/fetch.js'
import { useAppStore } from '@/stores/app.js'
import { defineStore } from 'pinia'

export const useApiStore = defineStore('api', {
  persist: true,
  state: () => ({
    token: null,
    role: null,
  }),
  actions: {
    /**
     * Fetches the license information from the API.
     * Communicates with the configured API endpoint to retrieve details about the license.
     * @return {Promise<Object>} A Promise that resolves to the license data returned by the API.
     */
    license() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/license`)
    },
    /**
     * Checks if the user is authenticated by verifying the existence of a token and a role.
     * @return {boolean} Returns true if both token and role are present; otherwise, false.
     */
    isAuthenticated() {
      return this.token && this.role
    },
    /**
     *
     * @returns {Promise<any>}
     */
    health() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/health`)
    },
    /**
     * Request login token
     * @param email
     * @returns {Promise<*>}
     */
    requestLogin(email) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/auth/request-login`, {
        email,
      })
    },
    /**
     * Login with token
     * @param email
     * @param token
     * @returns {Promise<*>}
     */
    login(email, token) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/auth/login`, {
        email,
        token,
      }).then(resp => {
        if (resp.success) {
          this.token = resp.data.token
          this.role = resp.data.role
        }
        return resp
      })
    },
    /**
     *
     * @returns {Promise<* | Response>}
     */
    logout() {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/auth/logout`).finally(() => {
        this.token = null
        this.role = null
      })
    },
    /**
     *
     */
    account() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/users/me`)
    },
    /**
     *
     */
    updateUser(user) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/users/${user.id}`, user)
    },
    /**
     *
     */
    getUsers() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/users`)
    },
    /**
     *
     * @param user
     */
    createUser(user) {
      return useFetch('PUT', `${useAppStore().getConfigValue('API_URL')}/api/v1/users`, user)
    },
    /**
     *
     * @param userId
     */
    deleteUser(userId) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/users/${userId}`)
    },
    /**
     *
     * @returns {Promise<* | Response>}
     */
    findFolders() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/folders`)
    },
    /**
     *
     * @param folderId
     * @param limit
     * @param skip
     * @param sort
     * @returns {Promise<* | Response>}
     */
    findFolderContents(folderId, limit = 100, skip = 0, sort = 'descending') {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/folders/${folderId}/contents?limit=${limit}&skip=${skip}&sort=${sort}`)
    },
    /**
     *
     * @param media
     * @param quiet
     * @returns {Promise<*>}
     */
    createMedia(media, quiet = false) {
      return useFetch('PUT', `${useAppStore().getConfigValue('API_URL')}/api/v1/media`, media, {}, { quiet })
    },
    /**
     *
     * @param media
     * @returns {Promise<*>}
     */
    updateMedia(media) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${media.id}`, media)
    },
    /**
     *
     * @param mediaId
     */
    deleteMedia(mediaId) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}`)
    },
    /**
     * Replace the physical file of an existing media with a freshly uploaded one.
     * The chunks must already be uploaded via uploadChunk under the given uploadId.
     * @param mediaId
     * @param uploadId
     * @param filename
     * @returns {Promise<any>}
     */
    replaceMedia(mediaId, uploadId, filename) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/replace`, {
        upload_id: uploadId,
        filename,
      })
    },
    /**
     * Upload a chunk of a file
     * @param filepath
     * @param uploadId
     * @param chunk
     * @param index
     * @param onProgressCallback
     * @returns {Promise<unknown>}
     */
    uploadChunk(filepath, uploadId, chunk, index, onProgressCallback = null) {
      return new Promise((resolve, reject) => {
        const formData = new FormData()
        const blob = chunk instanceof Blob ? chunk : new Blob([chunk])
        formData.append('file', blob)
        formData.append('index', index)
        formData.append('filepath', filepath)

        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/uploads/${uploadId}`)
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`)
        xhr.setRequestHeader('Accept', 'application/json')
        if (onProgressCallback) {
          xhr.upload.onprogress = onProgressCallback
        }

        xhr.onload = () => xhr.status === 200
          ? resolve(JSON.parse(xhr.responseText))
          : reject(xhr.responseText)
        xhr.onerror = () => reject('Fehler beim Upload')
        xhr.send(formData)
      })
    },
    /**
     * Complete a file upload
     * @param uploadId
     * @param filepath
     * @param filename
     * @returns {Promise<any>}
     */
    completeUpload(uploadId, filepath, filename) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/uploads/${uploadId}/complete`, {
        filepath,
        filename,
      })
    },
    /**
     *
     * @param file
     * @returns {Promise<string>}
     */
    async generateSha256Hash(file) {
      const buffer = await file.arrayBuffer()
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    },
    /**
     *
     * @param filepath
     * @param filename
     * @param hash
     * @param force
     * @returns {Promise<any>}
     */
    prepareUpload(filepath, filename, hash, force) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/uploads`, {
        filepath,
        filename,
        force,
        hash,
      })
    },
    mediaPermissions(mediaId) {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/permissions`)
    },
    /**
     *
     * @param mediaId
     * @param permissions Direct per-user permissions (dirty rows).
     * @param groupPermissions Per-group permissions (dirty rows).
     * @returns {Promise<any>}
     */
    updateMediaPermissions(mediaId, permissions, groupPermissions) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/permissions`, {
        permissions,
        group_permissions: groupPermissions,
      })
    },
    /**
     *
     * @param mediaId
     * @param isPublic
     * @returns {Promise<* | Response>}
     */
    shareMedia(mediaId, isPublic) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/share`, {
        public: isPublic,
      })
    },
    /**
     *
     * @param mediaId
     * @returns {Promise<* | Response>}
     */
    analyzeMedia(mediaId, ai_prompt) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/analyze`, {
        ai_prompt
      })
    },
    /**
     *
     * @param mediaId
     * @param property
     * @param value
     */
    updateMediaProperty(mediaId, property, value) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/updateProperty`, {
        property,
        value,
      })
    },
    /**
     *
     * @param query
     * @param searchMode
     * @returns {Promise<* | Response>}
     */
    search(query, searchMode) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/search`, {
        query,
        queryParams: {
          searchMode,
          limit: useAppStore().getConfigValue('SEARCH_LIMIT', 100),
          threshold: useAppStore().getConfigValue('SEARCH_THRESHOLD', 3),
        }
      })
    },
    quickSearch(limit, skip) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/quick-search`, {
        queryParams: {
          limit: limit || useAppStore().getConfigValue('SEARCH_LIMIT', 100),
          skip,
        }
      })
    },
    /**
     *
     * @param query
     */
    searchSharedMedia(query) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/searchSharedMedia`, {
        query,
        queryParams: {
          limit: useAppStore().getConfigValue('SEARCH_LIMIT', 100),
          threshold: useAppStore().getConfigValue('SEARCH_THRESHOLD', 0.5),
        }
      })
    },
    /**
     *
     * @param mediaId
     * @returns {Promise<any>}
     */
    findSharedMedia(mediaId) {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/shared`)
    },
    /**
     *
     * @param mediaId
     * @param destinationId
     * @returns {Promise<any>}
     */
    moveMedia(mediaId, destinationId) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/move`, {
        target_id: destinationId,
      })
    },
    /**
     *
     * @returns {Promise<any>}
     */
    findAiJobs() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/ai-jobs`)
    },
    /**
     *
     * @param id
     * @returns {Promise<any>}
     */
    deleteAiJob(id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/ai-jobs/${id}`)
    },
    /**
     *
     * @param id
     * @returns {Promise<* | Response>}
     */
    refreshMedia(id) {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${id}/refresh`)
    },
    copyMedia(id) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${id}/copy`)
    },
    /**
     *
     * @returns {Promise<any>}
     */
    findMediaAttributeSchema() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeSchema`)
    },
    /**
     *
     * @param schema
     * @returns {Promise<any>}
     */
    createMediaAttributeSchema(schema) {
      return useFetch('PUT', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeSchema`, schema)
    },
    /**
     *
     * @param schema
     * @returns {Promise<any>}
     */
    updateMediaAttributeSchema(schema) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeSchema/${schema.id}`, schema)
    },
    /**
     *
     * @param id
     * @returns {Promise<any>}
     */
    deleteMediaAttributeSchema(id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeSchema/${id}`)
    },
    /**
     * @returns {Promise<any>}
     */
    findMediaAttributeValueList() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeValueList`)
    },
    /**
     * @param list
     * @returns {Promise<any>}
     */
    createMediaAttributeValueList(list) {
      return useFetch('PUT', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeValueList`, list)
    },
    /**
     * @param list
     * @returns {Promise<any>}
     */
    updateMediaAttributeValueList(list) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeValueList/${list.id}`, list)
    },
    /**
     * @param id
     * @returns {Promise<any>}
     */
    deleteMediaAttributeValueList(id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/mediaAttributeValueList/${id}`)
    },
    /**
     *
     * @param id
     * @param queryForDigitalSignage
     * @returns {Promise<any>}
     */
    getMediaChildren(id, queryForDigitalSignage = false) {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${id}/children?digitalSignage=${queryForDigitalSignage}`)
    },
    /**
     * @returns {Promise<any>}
     * @param collection
     */
    createMediaCollection(collection) {
      return useFetch('PUT', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-collections`, {
        ...collection
      })
    },
    /**
     * @param collection
     * @returns {Promise<any>}
     */
    updateMediaCollection(collection) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-collections/${collection.id}`, {
        ...collection
      })
    },
    /**
     *
     * @param collectionId
     * @returns {Promise<any>}
     */
    deleteMediaCollection(collectionId) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-collections/${collectionId}`)
    },
    /**
     *
     * @param collectionId
     * @param mediaIds
     * @returns {Promise<any>}
     */
    updateMediaCollectionItems(collectionId, mediaIds) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-collections/${collectionId}/items`, {
        mediaIds,
        collectionId
      })
    },
    updateMediaRelation(sources, targets) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-relations`, {
        sources,
        targets
      })
    },

    // --- Social: comments ---------------------------------------------------

    getComments(mediaId) {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/comments`)
    },
    getSharedComments(mediaId) {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/shared-comments`)
    },
    postComment(mediaId, content, parentId = null, mentions = []) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/comments`, {
        content,
        parent_id: parentId,
        mentions,
      })
    },
    toggleCommentLike(mediaId, commentId) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/comments/${commentId}/like`)
    },
    // Toggle a like on the media itself (social module). Backend returns the
    // authoritative `{ liked, likes_count }`. Endpoint to be added server-side.
    likeMedia(mediaId) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/like`)
    },
    deleteComment(mediaId, commentId) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/comments/${commentId}`)
    },
    getMentionableUsers() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/users/mentionable`)
    },

    // --- Social: notifications ----------------------------------------------

    getNotifications() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/notifications`)
    },
    markNotificationRead(id) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/notifications/${id}/read`)
    },
    markAllNotificationsRead() {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/notifications/read-all`)
    },
    deleteNotification(id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/notifications/${id}`)
    },
    deleteAllNotifications() {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/notifications`)
    },

    // --- Social: subscriptions ----------------------------------------------

    getSubscriptions() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/subscriptions`)
    },
    subscribe(type, id) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/subscriptions`, {
        type,
        id,
      })
    },
    unsubscribe(type, id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/subscriptions`, {
        type,
        id,
      })
    },

    // --- Social: reported media ---------------------------------------------

    reportMedia(mediaId, reason) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/report`, {
        reason,
      })
    },
    getMediaReports(status = null) {
      const query = status ? `?status=${status}` : ''
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-reports${query}`)
    },
    resolveMediaReport(id, status) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-reports/${id}`, {
        status,
      })
    },
    deleteMediaReport(id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/media-reports/${id}`)
    },

    // --- Transcoder: derivates ----------------------------------------------

    getDerivateFormats() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/derivate-formats`)
    },
    getDerivates(mediaId) {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/derivates`)
    },
    createDerivate(mediaId, formatId) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/derivates/${formatId}`)
    },
    deleteDerivate(mediaId, formatId) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/derivates/${formatId}`)
    },
    // Authenticated blob download (route is JWT-gated, not a signed URL).
    async downloadDerivate(mediaId, formatId) {
      const headers = {}
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`
      }
      const resp = await fetch(
        `${useAppStore().getConfigValue('API_URL')}/api/v1/media/${mediaId}/derivates/${formatId}/download`,
        { headers },
      )
      if (!resp.ok) {
        return null
      }
      const disposition = resp.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename="?([^"]+)"?/)
      return { blob: await resp.blob(), filename: match?.[1] || 'derivate' }
    },

    // --- Admin: output formats ----------------------------------------------

    getOutputFormats() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/output-formats`)
    },
    createOutputFormat(payload) {
      return useFetch('PUT', `${useAppStore().getConfigValue('API_URL')}/api/v1/output-formats`, payload)
    },
    updateOutputFormat(id, payload) {
      return useFetch('POST', `${useAppStore().getConfigValue('API_URL')}/api/v1/output-formats/${id}`, payload)
    },
    deleteOutputFormat(id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/output-formats/${id}`)
    },

    // --- Admin: API keys ----------------------------------------------------

    getApiKeys() {
      return useFetch('GET', `${useAppStore().getConfigValue('API_URL')}/api/v1/api-keys`)
    },
    createApiKey(payload) {
      return useFetch('PUT', `${useAppStore().getConfigValue('API_URL')}/api/v1/api-keys`, payload)
    },
    deleteApiKey(id) {
      return useFetch('DELETE', `${useAppStore().getConfigValue('API_URL')}/api/v1/api-keys/${id}`)
    },
  },
})